/**
 * HybridEditor — V14.62 Sprint 20 (Lab).
 *
 * Évolutions Sprint 20 :
 *   • Plan de fond = vraie image utilisateur (BackgroundDropzone) OU fallback SVG
 *   • Zoom (molette) + Pan (Espace + drag) sur le Stage Konva
 *   • Outil Porte (P) avec rendu arc de cercle distinct des fenêtres
 *
 * Architecture inchangée :
 *   Layer 1 (background, non-interactif, opacity 0.3)
 *   Layer 2 (vector overlay, opaque, interactif)
 */
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Stage, Layer, Image as KImage, Line, Circle, Group, Rect, Text, Arc } from 'react-konva';
import { toast } from 'sonner';
import { useGeometryStore, TOOLS, orthoSnapPoint, STAIRS_KINDS, wallKindFor } from './useGeometryStore';
import ElementInspector from './ElementInspector';

const CANVAS_W = 900;
const CANVAS_H = 560;
const MIN_SCALE = 0.2;
const MAX_SCALE = 8;

// ─────────────────────────────────────────────────────────────────────
// Helpers géométriques
// ─────────────────────────────────────────────────────────────────────
const distToSegment = (px, py, x1, y1, x2, y2) => {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy || 1;
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const ix = x1 + t * dx, iy = y1 + t * dy;
  return { d: Math.hypot(px - ix, py - iy), t };
};

const wallAngleDeg = (w) =>
  (Math.atan2(w.y2 - w.y1, w.x2 - w.x1) * 180) / Math.PI;
const wallLength = (w) => Math.hypot(w.x2 - w.x1, w.y2 - w.y1);

const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

// ─────────────────────────────────────────────────────────────────────
// Background "fake plan" — generated as an inline SVG dataURI so the
// component is self-contained (no remote asset needed for the PoC).
// ─────────────────────────────────────────────────────────────────────
const FAKE_PLAN_DATA_URL = (() => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${CANVAS_W}" height="${CANVAS_H}" viewBox="0 0 ${CANVAS_W} ${CANVAS_H}">
      <rect width="100%" height="100%" fill="#f7f3ea"/>
      <g stroke="#9a8c6e" stroke-width="2" fill="none">
        <rect x="100" y="100" width="440" height="300"/>
        <line x1="320" y1="100" x2="320" y2="400"/>
        <line x1="100" y1="240" x2="320" y2="240"/>
        <line x1="320" y1="200" x2="540" y2="200"/>
      </g>
      <g font-family="Georgia, serif" font-size="13" fill="#7a6c4f" font-style="italic">
        <text x="180" y="180">Séjour</text>
        <text x="200" y="320">Cuisine</text>
        <text x="380" y="155">Chambre</text>
        <text x="380" y="310">SdB</text>
      </g>
      <g stroke="#bca988" stroke-width="1.5" stroke-dasharray="6 4" fill="none">
        <path d="M 180 100 A 30 30 0 0 1 210 130 L 180 130 Z"/>
        <path d="M 360 240 A 25 25 0 0 1 385 265 L 360 265 Z"/>
      </g>
    </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
})();

// ─────────────────────────────────────────────────────────────────────
// Sub-component : a single wall in the vector overlay
// ─────────────────────────────────────────────────────────────────────
const WallShape = ({ wall, isSelected, onClick, onEndpointDrag, onBodyDrag }) => {
  const { id, x1, y1, x2, y2, thickness } = wall;
  const angle = wallAngleDeg(wall);
  const len = wallLength(wall);
  const def = wallKindFor(wall.kind);

  // Convert the kind color to an opaque stroke + a translucent fill so the
  // wall reads as a "thick segment" while remaining easy to click through.
  const stroke = def.color;
  const fill = isSelected
    ? `${def.color}88`   // ~53% alpha when selected
    : `${def.color}55`;  // ~33% alpha otherwise

  return (
    <Group data-testid={`wall-group-${id}`}>
      <Rect
        x={x1}
        y={y1 - thickness / 2}
        width={len}
        height={thickness}
        rotation={angle}
        offsetX={0}
        offsetY={0}
        fill={fill}
        stroke={stroke}
        strokeWidth={isSelected ? def.strokeWidth + 1 : def.strokeWidth}
        shadowColor={stroke}
        shadowBlur={isSelected ? 14 : 0}
        shadowOpacity={0.6}
        draggable
        onDragMove={(e) => {
          const node = e.target;
          const dx = node.x() - x1;
          const dy = node.y() - (y1 - thickness / 2);
          // Reset visual offset → store carries the truth
          node.position({ x: x1, y: y1 - thickness / 2 });
          onBodyDrag(dx, dy);
        }}
        onClick={(e) => {
          // Let the click bubble up to the Stage handler for tools that
          // need the precise click position (placing menuiseries / stairs)
          // or for the wall chain logic (where the user might click on an
          // existing wall corner to close or attach to it). Only the
          // SELECT and DELETE tools handle the click on the shape itself.
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) {
            e.cancelBubble = true;
          }
          onClick();
        }}
        onTap={(e) => {
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) {
            e.cancelBubble = true;
          }
          onClick();
        }}
        // Use crosshair when dragging is the active tool
        onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'move'; }}
        onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
      />
      {/* Endpoint handles — only visible when selected */}
      {isSelected && (
        <>
          <Circle
            x={x1} y={y1} radius={7}
            fill="#0ea5e9"
            stroke="#fff" strokeWidth={1.5}
            draggable
            onDragMove={(e) => onEndpointDrag('a', e.target.x(), e.target.y())}
            onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
            onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
            data-testid={`wall-${id}-handle-a`}
          />
          <Circle
            x={x2} y={y2} radius={7}
            fill="#0ea5e9"
            stroke="#fff" strokeWidth={1.5}
            draggable
            onDragMove={(e) => onEndpointDrag('b', e.target.x(), e.target.y())}
            onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
            onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
            data-testid={`wall-${id}-handle-b`}
          />
        </>
      )}
    </Group>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sprint 31 — Skew edge (ligne de brisure pour mansardes)
// Dashed orange line, selectable + draggable body & endpoints.
// ─────────────────────────────────────────────────────────────────────
const SkewEdgeShape = ({ edge, isSelected, onClick, onEndpointDrag, onBodyDrag }) => {
  const { id, x1, y1, x2, y2 } = edge;
  const stroke = '#fb923c';  // orange-400
  const strokeW = isSelected ? 3 : 2;
  const points = [x1, y1, x2, y2];
  return (
    <Group data-testid={`skew-group-${id}`}>
      <Line
        points={points}
        stroke={stroke}
        strokeWidth={strokeW}
        dash={[10, 6]}
        lineCap="round"
        shadowColor={stroke}
        shadowBlur={isSelected ? 12 : 0}
        shadowOpacity={0.5}
        hitStrokeWidth={12}
        draggable
        onDragMove={(e) => {
          const node = e.target;
          const dx = node.x();
          const dy = node.y();
          node.position({ x: 0, y: 0 });
          if (dx || dy) onBodyDrag(dx, dy);
        }}
        onClick={(e) => {
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) e.cancelBubble = true;
          onClick();
        }}
        onTap={(e) => {
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) e.cancelBubble = true;
          onClick();
        }}
        onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'move'; }}
        onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
      />
      {isSelected && (
        <>
          <Circle
            x={x1} y={y1} radius={6} fill="#fb923c" stroke="#fff" strokeWidth={1.5}
            draggable
            onDragMove={(e) => onEndpointDrag('a', e.target.x(), e.target.y())}
            onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
            onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
            data-testid={`skew-${id}-handle-a`}
          />
          <Circle
            x={x2} y={y2} radius={6} fill="#fb923c" stroke="#fff" strokeWidth={1.5}
            draggable
            onDragMove={(e) => onEndpointDrag('b', e.target.x(), e.target.y())}
            onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
            onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
            data-testid={`skew-${id}-handle-b`}
          />
        </>
      )}
    </Group>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sprint 31 Phase 2 — Velux (fenêtre de toit sur mansarde)
// Rectangle bleu translucide placé perpendiculairement à la ligne de
// brisure au point t ∈ [0..1]. La largeur affichée est en px (converti
// depuis width_m via le scale courant si dispo).
// ─────────────────────────────────────────────────────────────────────
const VeluxShape = ({ velux, skew, scale, isSelected, onClick, onDragT }) => {
  if (!skew) return null;
  const { x1, y1, x2, y2 } = skew;
  const dxL = x2 - x1, dyL = y2 - y1;
  const len = Math.hypot(dxL, dyL);
  if (len < 1) return null;
  const cx = x1 + velux.t * dxL;
  const cy = y1 + velux.t * dyL;
  // Perpendicular direction (unit).
  const nx = -dyL / len, ny = dxL / len;
  // Velux width in px (fallback : 40 px when uncalibrated).
  const wPx = scale > 0 ? (velux.width_m / scale) : 40;
  const hPx = scale > 0 ? (velux.height_m / scale) : 24;
  // Points of the rectangle : centered on (cx, cy), long side ⊥ skew.
  const ax = dxL / len, ay = dyL / len;
  const hw = wPx / 2;
  const hh = hPx / 2;
  const px1 = cx - hw * nx - hh * ax;
  const py1 = cy - hw * ny - hh * ay;
  const px2 = cx + hw * nx - hh * ax;
  const py2 = cy + hw * ny - hh * ay;
  const px3 = cx + hw * nx + hh * ax;
  const py3 = cy + hw * ny + hh * ay;
  const px4 = cx - hw * nx + hh * ax;
  const py4 = cy - hw * ny + hh * ay;
  return (
    <Group data-testid={`velux-group-${velux.id}`}>
      <Line
        points={[px1, py1, px2, py2, px3, py3, px4, py4, px1, py1]}
        closed
        fill="rgba(56, 189, 248, 0.35)"
        stroke={isSelected ? '#38bdf8' : '#0ea5e9'}
        strokeWidth={isSelected ? 3 : 2}
        onClick={(e) => {
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) e.cancelBubble = true;
          onClick();
        }}
        onTap={(e) => {
          const t = useGeometryStore.getState().tool;
          if (t === TOOLS.SELECT || t === TOOLS.DELETE) e.cancelBubble = true;
          onClick();
        }}
        onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'move'; }}
        onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
      />
      {isSelected && (
        <Circle
          x={cx} y={cy} radius={5} fill="#38bdf8" stroke="#fff" strokeWidth={1.5}
          draggable
          onDragMove={(e) => {
            const nx2 = e.target.x(), ny2 = e.target.y();
            const t = ((nx2 - x1) * dxL + (ny2 - y1) * dyL) / (dxL * dxL + dyL * dyL);
            onDragT(Math.max(0, Math.min(1, t)));
            // Snap back to the line
            const cx2 = x1 + Math.max(0, Math.min(1, t)) * dxL;
            const cy2 = y1 + Math.max(0, Math.min(1, t)) * dyL;
            e.target.position({ x: cx2, y: cy2 });
          }}
          onMouseEnter={(e) => { e.target.getStage().container().style.cursor = 'grab'; }}
          onMouseLeave={(e) => { e.target.getStage().container().style.cursor = 'default'; }}
          data-testid={`velux-${velux.id}-handle`}
        />
      )}
    </Group>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-component : a window placed on a wall (parametric position t)
// ─────────────────────────────────────────────────────────────────────
const WindowShape = ({ window: win, wall, isSelected, onSelect }) => {
  if (!wall) return null;
  const len = wallLength(wall);
  const angle = wallAngleDeg(wall);
  const half = (win.width * len) / 2;
  const cx = wall.x1 + (wall.x2 - wall.x1) * win.t;
  const cy = wall.y1 + (wall.y2 - wall.y1) * win.t;
  const startX = cx - half * Math.cos((angle * Math.PI) / 180);
  const startY = cy - half * Math.sin((angle * Math.PI) / 180);
  return (
    <Rect
      x={startX}
      y={startY - wall.thickness / 2}
      width={half * 2}
      height={wall.thickness}
      rotation={angle}
      fill={isSelected ? 'rgba(56, 189, 248, 0.85)' : 'rgba(56, 189, 248, 0.5)'}
      stroke={isSelected ? '#0ea5e9' : '#7dd3fc'}
      strokeWidth={isSelected ? 2.2 : 1.2}
      shadowColor="#0ea5e9"
      shadowBlur={isSelected ? 10 : 0}
      shadowOpacity={0.7}
      data-testid={`window-${win.id}`}
      onClick={(e) => {
        const t = useGeometryStore.getState().tool;
        if (t === TOOLS.SELECT) { e.cancelBubble = true; onSelect && onSelect(); }
      }}
      onTap={(e) => {
        const t = useGeometryStore.getState().tool;
        if (t === TOOLS.SELECT) { e.cancelBubble = true; onSelect && onSelect(); }
      }}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-component : stairs / trémie — rectangle zone with a glyph that
// changes by kind to make it readable at a glance on the canvas.
//
//   • Straight  → parallel step lines + arrow
//   • Turning   → L-shaped arrow (90° turn)
//   • Spiral    → concentric arcs
// ─────────────────────────────────────────────────────────────────────
const StairsShape = ({ stairs, isSelected, onSelect }) => {
  const { id, x, y, w, h, kind } = stairs;
  const stroke = isSelected ? '#c084fc' : '#a855f7';
  const fill = isSelected ? 'rgba(168,85,247,0.25)' : 'rgba(168,85,247,0.12)';

  // Sprint 28+ — drag-to-move + resize handles. They are only rendered when
  // the stairs are selected so the canvas stays readable in normal mode.
  // The handles never propagate their drag to the underlying rectangle —
  // they directly patch the store (x,y for move, w,h for resize).
  const updateStairs = useGeometryStore((s) => s.updateStairs);
  const tool = useGeometryStore((s) => s.tool);
  const canEditHandles = isSelected && tool === TOOLS.SELECT;

  // Glyph rendered inside the rectangle. Computed via Konva primitives
  // (Line + Arc) — no SVG required.
  const glyph = (() => {
    if (kind === STAIRS_KINDS.STRAIGHT) {
      // 5 parallel step lines + arrow heading down
      const steps = 5;
      const lines = [];
      for (let i = 1; i < steps; i += 1) {
        const py = y + (h * i) / steps;
        lines.push(
          <Line key={`s-${id}-${i}`}
                points={[x + 6, py, x + w - 6, py]}
                stroke={stroke} strokeWidth={1} opacity={0.7}
                listening={false} />,
        );
      }
      // Arrow direction (down)
      const ax = x + w / 2;
      lines.push(
        <Line key={`s-${id}-arrow`}
              points={[ax, y + 6, ax, y + h - 8, ax - 5, y + h - 14,
                       ax, y + h - 8, ax + 5, y + h - 14]}
              stroke={stroke} strokeWidth={1.5} opacity={0.9}
              listening={false} />,
      );
      return lines;
    }
    if (kind === STAIRS_KINDS.TURNING) {
      // L-shape arrow : starts top, goes down to mid-height, turns right
      const midY = y + h / 2;
      return (
        <Line key={`t-${id}`}
              points={[x + w * 0.3, y + 8,
                       x + w * 0.3, midY,
                       x + w - 8, midY,
                       x + w - 14, midY - 5,
                       x + w - 8, midY,
                       x + w - 14, midY + 5]}
              stroke={stroke} strokeWidth={1.6} opacity={0.9}
              listening={false} />
      );
    }
    // Spiral — 3 concentric arcs
    const cx = x + w / 2;
    const cy = y + h / 2;
    const r = Math.min(w, h) / 2 - 6;
    return [0.4, 0.7, 1].map((k, i) => (
      <Arc key={`sp-${id}-${i}`}
           x={cx} y={cy}
           innerRadius={r * k - 1.5}
           outerRadius={r * k + 1.5}
           angle={270}
           rotation={-90 + i * 30}
           stroke={stroke} strokeWidth={1.2}
           opacity={0.85}
           fill="transparent"
           listening={false} />
    ));
  })();

  const onStairsClick = (e) => {
    const t = useGeometryStore.getState().tool;
    if (t === TOOLS.SELECT) { e.cancelBubble = true; onSelect && onSelect(); }
  };

  return (
    <Group data-testid={`stairs-${id}`}>
      <Rect
        x={x} y={y} width={w} height={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={isSelected ? 2.4 : 1.4}
        dash={[6, 3]}
        cornerRadius={3}
        shadowColor={stroke}
        shadowBlur={isSelected ? 12 : 0}
        shadowOpacity={0.6}
        onClick={onStairsClick}
        onTap={onStairsClick}
      />
      {glyph}
      <Text
        listening={false}
        x={x + 4} y={y + 4}
        text={kind === STAIRS_KINDS.STRAIGHT ? 'ESC ↓'
            : kind === STAIRS_KINDS.TURNING  ? 'ESC ⟲'
            : 'ESC ↻'}
        fontSize={9}
        fontStyle="bold"
        fill="#d8b4fe"
      />
      {/* Move handle (top-left) — draggable circle that patches x/y. */}
      {canEditHandles && (
        <Circle
          x={x} y={y}
          radius={6}
          fill="#0b1220"
          stroke="#c084fc"
          strokeWidth={1.5}
          draggable
          data-testid={`stairs-${id}-move-handle`}
          onMouseEnter={(e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'move'; }}
          onMouseLeave={(e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
          onDragMove={(e) => {
            // Live patch — the rectangle follows the move handle in real time.
            const nx = Math.round(e.target.x());
            const ny = Math.round(e.target.y());
            updateStairs(id, { x: nx, y: ny });
          }}
        />
      )}
      {/* Resize handle (bottom-right) — draggable circle that patches w/h. */}
      {canEditHandles && (
        <Circle
          x={x + w} y={y + h}
          radius={6}
          fill="#0b1220"
          stroke="#c084fc"
          strokeWidth={1.5}
          draggable
          data-testid={`stairs-${id}-resize-handle`}
          onMouseEnter={(e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'nwse-resize'; }}
          onMouseLeave={(e) => { const s = e.target.getStage(); if (s) s.container().style.cursor = 'default'; }}
          onDragMove={(e) => {
            const nw = Math.max(20, Math.round(e.target.x() - x));
            const nh = Math.max(20, Math.round(e.target.y() - y));
            updateStairs(id, { w: nw, h: nh });
          }}
        />
      )}
    </Group>
  );
};
const DoorShape = ({ door, wall, isSelected, onSelect }) => {
  if (!wall) return null;
  const len = wallLength(wall);
  const angle = wallAngleDeg(wall);
  const half = (door.width * len) / 2;
  const cx = wall.x1 + (wall.x2 - wall.x1) * door.t;
  const cy = wall.y1 + (wall.y2 - wall.y1) * door.t;
  const rad = (angle * Math.PI) / 180;

  // Opening rectangle (empty look — just a thin outline)
  const startX = cx - half * Math.cos(rad);
  const startY = cy - half * Math.sin(rad);

  // Door swing arc starts at one edge of the opening, perpendicular to
  // the wall, with a radius equal to the opening width.
  const hingeX = cx - half * Math.cos(rad);
  const hingeY = cy - half * Math.sin(rad);

  const onShapeClick = (e) => {
    const t = useGeometryStore.getState().tool;
    if (t === TOOLS.SELECT) { e.cancelBubble = true; onSelect && onSelect(); }
  };

  return (
    <Group data-testid={`door-${door.id}`}>
      {/* Opening — dashed amber outline so it reads "door" not "wall" */}
      <Rect
        x={startX}
        y={startY - wall.thickness / 2}
        width={half * 2}
        height={wall.thickness}
        rotation={angle}
        fill={isSelected ? 'rgba(245, 158, 11, 0.18)' : 'rgba(2,6,12,0.55)'}
        stroke={isSelected ? '#f59e0b' : '#f59e0b'}
        strokeWidth={isSelected ? 2.2 : 1.4}
        dash={[5, 3]}
        shadowColor="#f59e0b"
        shadowBlur={isSelected ? 10 : 0}
        shadowOpacity={0.7}
        onClick={onShapeClick}
        onTap={onShapeClick}
      />
      {/* Swing arc (90°) — the visual signature of a door in CAD */}
      <Arc
        x={hingeX}
        y={hingeY}
        innerRadius={0}
        outerRadius={half * 2}
        angle={90}
        rotation={angle}
        stroke="#fbbf24"
        strokeWidth={1.5}
        fill="rgba(251,191,36,0.08)"
        listening={false}
      />
      {/* Hinge point */}
      <Circle x={hingeX} y={hingeY} radius={2.5} fill="#fbbf24" listening={false} />
    </Group>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sub-component : Terrain — closed polygon for the property boundary.
// Rendered with a sand-yellow dashed stroke + faint green fill so it
// reads as "ground / plot" even on top of a satellite image.
// ─────────────────────────────────────────────────────────────────────
const TerrainShape = ({ points, onClick }) => {
  if (!points || points.length < 3) return null;
  const flat = [];
  points.forEach((p) => { flat.push(p.x, p.y); });
  return (
    <Group data-testid="terrain-shape">
      <Line
        points={flat}
        closed
        stroke="#fbbf24"
        strokeWidth={2}
        dash={[10, 5]}
        fill="rgba(34,197,94,0.10)"
        onClick={(e) => { e.cancelBubble = true; onClick && onClick(); }}
        onTap={(e) => { e.cancelBubble = true; onClick && onClick(); }}
      />
      {points.map((p, i) => (
        <Circle key={`tv-${i}`} x={p.x} y={p.y} radius={3.5}
                fill="#fbbf24" stroke="#78350f" strokeWidth={1}
                listening={false} />
      ))}
    </Group>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main editor
// ─────────────────────────────────────────────────────────────────────
const HybridEditor = () => {
  const walls       = useGeometryStore((s) => s.walls);
  const windows     = useGeometryStore((s) => s.windows);
  const doors       = useGeometryStore((s) => s.doors);
  const stairs      = useGeometryStore((s) => s.stairs);
  const skewEdges   = useGeometryStore((s) => s.skew_edges);
  const veluxes     = useGeometryStore((s) => s.velux);
  const tool        = useGeometryStore((s) => s.tool);
  const selectedId  = useGeometryStore((s) => s.selectedId);
  const drawingFrom = useGeometryStore((s) => s.drawingFrom);
  const chainStart  = useGeometryStore((s) => s.chainStart);
  const drawWallKind = useGeometryStore((s) => s.drawWallKind);
  const calibrationFrom = useGeometryStore((s) => s.calibrationFrom);
  const calibration     = useGeometryStore((s) => s.calibration);
  const scale           = useGeometryStore((s) => s.scale_m_per_px);
  const stairsFrom     = useGeometryStore((s) => s.stairsFrom);
  const terrain        = useGeometryStore((s) => s.terrain);
  const terrainDraft   = useGeometryStore((s) => s.terrainDraft);
  const backgroundImage = useGeometryStore((s) => s.backgroundImage);
  const viewport        = useGeometryStore((s) => s.viewport);
  const selectWall          = useGeometryStore((s) => s.selectWall);
  const clearSelection      = useGeometryStore((s) => s.clearSelection);
  const updateWallEndpoint  = useGeometryStore((s) => s.updateWallEndpoint);
  const translateWall       = useGeometryStore((s) => s.translateWall);
  const deleteWall          = useGeometryStore((s) => s.deleteWall);
  const placeWindow         = useGeometryStore((s) => s.placeWindow);
  const placeDoor           = useGeometryStore((s) => s.placeDoor);
  const addWall             = useGeometryStore((s) => s.addWall);
  const setDrawingFrom      = useGeometryStore((s) => s.setDrawingFrom);
  const setChainStart       = useGeometryStore((s) => s.setChainStart);
  const setCalibrationFrom  = useGeometryStore((s) => s.setCalibrationFrom);
  const beginCalibration    = useGeometryStore((s) => s.beginCalibration);
  const setStairsFrom       = useGeometryStore((s) => s.setStairsFrom);
  const beginStairs         = useGeometryStore((s) => s.beginStairs);
  const deleteStairs        = useGeometryStore((s) => s.deleteStairs);
  const pushTerrainVertex   = useGeometryStore((s) => s.pushTerrainVertex);
  const commitTerrain       = useGeometryStore((s) => s.commitTerrain);
  const clearTerrain        = useGeometryStore((s) => s.clearTerrain);
  // Sprint 31 — Skew edges (mansardes)
  const addSkewEdge         = useGeometryStore((s) => s.addSkewEdge);
  const updateSkewEdgeEndpoint = useGeometryStore((s) => s.updateSkewEdgeEndpoint);
  const translateSkewEdge   = useGeometryStore((s) => s.translateSkewEdge);
  const deleteSkewEdge      = useGeometryStore((s) => s.deleteSkewEdge);
  // Sprint 31 Phase 2 — Velux
  const addVelux            = useGeometryStore((s) => s.addVelux);
  const updateVelux         = useGeometryStore((s) => s.updateVelux);
  const deleteVelux         = useGeometryStore((s) => s.deleteVelux);
  const setViewport         = useGeometryStore((s) => s.setViewport);
  const resetViewport       = useGeometryStore((s) => s.resetViewport);

  // Track Shift state — used for ortho-snap during DRAW_WALL.
  // Track Space — used for pan mode.
  const shiftDownRef = useRef(false);
  const spaceDownRef = useRef(false);
  useEffect(() => {
    const onDown = (e) => {
      if (e.key === 'Shift') shiftDownRef.current = true;
      if (e.code === 'Space' && !isEditableTarget(e.target)) {
        spaceDownRef.current = true;
        e.preventDefault();
      }
    };
    const onUp = (e) => {
      if (e.key === 'Shift') shiftDownRef.current = false;
      if (e.code === 'Space') spaceDownRef.current = false;
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, []);

  // Load background as native HTMLImageElement for Konva. Falls back to the
  // SVG sample plan when the user hasn't imported one yet — so the canvas is
  // never blank.
  const [bgImage, setBgImage] = useState(null);
  useEffect(() => {
    const img = new Image();
    const src = backgroundImage?.dataUrl || FAKE_PLAN_DATA_URL;
    img.src = src;
    img.onload = () => setBgImage(img);
  }, [backgroundImage]);

  const stageRef = useRef(null);

  const wallById = useMemo(() => {
    const m = new Map();
    walls.forEach((w) => m.set(w.id, w));
    return m;
  }, [walls]);

  // Convert a stage-coordinate position to local canvas coords (i.e. undo
  // current viewport transform).
  const toCanvasPos = useCallback((stagePos) => ({
    x: (stagePos.x - viewport.x) / viewport.scale,
    y: (stagePos.y - viewport.y) / viewport.scale,
  }), [viewport]);

  // Snap a free point to the nearest wall endpoint within `radius` canvas
  // pixels. Returns the unmodified point + `snapped=false` when nothing is
  // close enough. Used while drawing walls so the user can easily chain
  // segments and form clean junctions.
  const snapToWallEndpoint = useCallback((x, y, radius = 12) => {
    const r2 = radius * radius;
    let best = null;
    let bestD = r2;
    walls.forEach((w) => {
      const d1 = (w.x1 - x) ** 2 + (w.y1 - y) ** 2;
      if (d1 < bestD) { bestD = d1; best = { x: w.x1, y: w.y1 }; }
      const d2 = (w.x2 - x) ** 2 + (w.y2 - y) ** 2;
      if (d2 < bestD) { bestD = d2; best = { x: w.x2, y: w.y2 }; }
    });
    return best ? { x: best.x, y: best.y, snapped: true } : { x, y, snapped: false };
  }, [walls]);

  // Stage click router : routes the click depending on the active tool.
  // We read fresh state via `getState()` to avoid stale-closure traps when
  // two clicks happen in rapid succession (React batches re-renders).
  const handleStageClick = (e) => {
    // Ignore clicks during pan (Space-drag) — they are consumed by the
    // stage drag handler, not by tool logic.
    if (spaceDownRef.current) return;

    const stage = e.target.getStage();
    const stagePos = stage.getPointerPosition();
    if (!stagePos) return;
    const pos = toCanvasPos(stagePos);
    const clickedEmpty = e.target === stage;
    const st = useGeometryStore.getState();
    const curTool = st.tool;
    const curDrawingFrom = st.drawingFrom;
    const curCalibrationFrom = st.calibrationFrom;

    if (curTool === TOOLS.DRAW_WALL) {
      // Chain-drawing mode (Sprint 25) — each click adds a wall segment
      // whose start point is the previous segment's end. Endpoint snap
      // is applied so the user can attach to existing wall corners.
      const snapped = snapToWallEndpoint(pos.x, pos.y, 12);
      let endX = snapped.x, endY = snapped.y;

      if (!curDrawingFrom) {
        // First click of the chain — store both `drawingFrom` and
        // `chainStart` (for closure on double-click or near-click).
        setDrawingFrom({ x: Math.round(endX), y: Math.round(endY) });
        setChainStart({ x: Math.round(endX), y: Math.round(endY) });
        return;
      }

      // Closing the chain by clicking near the chain start (within 14 px).
      const curChainStart = st.chainStart;
      if (curChainStart) {
        const dStart = Math.hypot(endX - curChainStart.x, endY - curChainStart.y);
        if (dStart <= 14
            && Math.hypot(curDrawingFrom.x - curChainStart.x, curDrawingFrom.y - curChainStart.y) > 5) {
          addWall(curDrawingFrom.x, curDrawingFrom.y, curChainStart.x, curChainStart.y, {
            kind: useGeometryStore.getState().drawWallKind,
          });
          setDrawingFrom(null);
          setChainStart(null);
          toast.success('Pièce fermée');
          return;
        }
      }

      // Ortho-snap (Shift) anchors on the current chain head.
      if (shiftDownRef.current && !snapped.snapped) {
        const o = orthoSnapPoint(curDrawingFrom.x, curDrawingFrom.y, endX, endY);
        endX = o.x; endY = o.y;
      }
      // Bail out if the user clicked the same point twice (zero-length).
      if (Math.hypot(endX - curDrawingFrom.x, endY - curDrawingFrom.y) < 3) {
        return;
      }
      addWall(curDrawingFrom.x, curDrawingFrom.y, endX, endY, {
        kind: useGeometryStore.getState().drawWallKind,
      });
      // Continue the chain : the new endpoint becomes the next chain head.
      setDrawingFrom({ x: Math.round(endX), y: Math.round(endY) });
      toast.success(
        snapped.snapped
          ? 'Mur ajouté (snap)'
          : (shiftDownRef.current ? 'Mur ajouté (ortho)' : 'Mur ajouté'),
      );
      return;
    }

    // Sprint 31 Phase 2 — Placement d'un Velux : clic sur une ligne de
    // brisure existante ; la position est projetée sur la ligne (t ∈ [0..1]).
    if (curTool === TOOLS.PLACE_VELUX) {
      if (!skewEdges.length) {
        toast.error('Aucune ligne de brisure — trace-en une d\'abord (outil B).');
        return;
      }
      let best = null;
      for (const e of skewEdges) {
        const dxL = e.x2 - e.x1, dyL = e.y2 - e.y1;
        const len2 = dxL * dxL + dyL * dyL;
        if (len2 < 1) continue;
        const t = Math.max(0, Math.min(1, ((pos.x - e.x1) * dxL + (pos.y - e.y1) * dyL) / len2));
        const px = e.x1 + t * dxL;
        const py = e.y1 + t * dyL;
        const d = Math.hypot(pos.x - px, pos.y - py);
        if (best === null || d < best.d) best = { d, edge: e, t };
      }
      if (!best || best.d > 24) {
        toast.error('Clique plus près d\'une ligne de brisure (≤ 24 px).');
        return;
      }
      addVelux(best.edge.id, best.t);
      toast.success('Velux ajouté sur la mansarde');
      return;
    }

    // Sprint 31 — Ligne de brisure (mansarde). 2 clics : point A puis point B.
    if (curTool === TOOLS.DRAW_SKEW) {
      const snapped = snapToWallEndpoint(pos.x, pos.y, 12);
      let endX = snapped.x, endY = snapped.y;
      if (!curDrawingFrom) {
        setDrawingFrom({ x: Math.round(endX), y: Math.round(endY) });
        return;
      }
      if (shiftDownRef.current && !snapped.snapped) {
        const o = orthoSnapPoint(curDrawingFrom.x, curDrawingFrom.y, endX, endY);
        endX = o.x; endY = o.y;
      }
      if (Math.hypot(endX - curDrawingFrom.x, endY - curDrawingFrom.y) < 3) {
        return;
      }
      addSkewEdge(curDrawingFrom.x, curDrawingFrom.y, endX, endY);
      setDrawingFrom(null);
      toast.success('Ligne de brisure ajoutée');
      return;
    }

    if (curTool === TOOLS.CALIBRATE) {
      if (!curCalibrationFrom) {
        setCalibrationFrom({ x: Math.round(pos.x), y: Math.round(pos.y) });
      } else {
        let endX = pos.x, endY = pos.y;
        if (shiftDownRef.current) {
          const snapped = orthoSnapPoint(curCalibrationFrom.x, curCalibrationFrom.y, pos.x, pos.y);
          endX = snapped.x; endY = snapped.y;
        }
        beginCalibration(curCalibrationFrom.x, curCalibrationFrom.y, Math.round(endX), Math.round(endY));
      }
      return;
    }

    if (curTool === TOOLS.PLACE_STAIRS) {
      const curStairsFrom = st.stairsFrom;
      if (!curStairsFrom) {
        setStairsFrom({ x: Math.round(pos.x), y: Math.round(pos.y) });
      } else {
        // Normalize so width/height are always positive even when the user
        // dragged from bottom-right to top-left.
        const x1 = Math.min(curStairsFrom.x, pos.x);
        const y1 = Math.min(curStairsFrom.y, pos.y);
        const w  = Math.abs(pos.x - curStairsFrom.x);
        const h  = Math.abs(pos.y - curStairsFrom.y);
        if (w < 8 || h < 8) {
          toast.message('Zone trop petite — tracez un rectangle plus large');
          return;
        }
        beginStairs(x1, y1, w, h);
      }
      return;
    }

    if (curTool === TOOLS.DRAW_TERRAIN) {
      const draft = st.terrainDraft;
      // Closing the polygon by clicking near the first vertex (within 12 px).
      if (draft.length >= 3) {
        const first = draft[0];
        const distFirst = Math.hypot(pos.x - first.x, pos.y - first.y);
        if (distFirst <= 12) {
          commitTerrain();
          toast.success('Terrain fermé');
          return;
        }
      }
      pushTerrainVertex(pos.x, pos.y);
      return;
    }

    if (curTool === TOOLS.PLACE_WINDOW || curTool === TOOLS.PLACE_DOOR) {
      const curWalls = st.walls;
      let best = null;
      for (const w of curWalls) {
        const r = distToSegment(pos.x, pos.y, w.x1, w.y1, w.x2, w.y2);
        if (r.d < 14 && (!best || r.d < best.d)) best = { wallId: w.id, t: r.t, d: r.d };
      }
      if (best) {
        if (curTool === TOOLS.PLACE_WINDOW) {
          placeWindow(best.wallId, best.t);
          toast.success('Fenêtre placée');
        } else {
          placeDoor(best.wallId, best.t);
          toast.success('Porte placée');
        }
      } else {
        toast.message(curTool === TOOLS.PLACE_WINDOW
          ? 'Clic sur un mur pour placer une fenêtre'
          : 'Clic sur un mur pour placer une porte');
      }
      return;
    }

    if (curTool === TOOLS.DELETE) {
      const curWalls = st.walls;
      const curStairs = st.stairs;
      // First check if click hit a stairs rectangle — they are larger targets.
      const hitStair = curStairs.find((s2) =>
        pos.x >= s2.x && pos.x <= s2.x + s2.w
        && pos.y >= s2.y && pos.y <= s2.y + s2.h);
      if (hitStair) {
        deleteStairs(hitStair.id);
        toast.success('Escalier supprimé');
        return;
      }
      let best = null;
      for (const w of curWalls) {
        const r = distToSegment(pos.x, pos.y, w.x1, w.y1, w.x2, w.y2);
        if (r.d < 14 && (!best || r.d < best.d)) best = { wallId: w.id, d: r.d };
      }
      if (best) {
        deleteWall(best.wallId);
        toast.success('Mur supprimé');
      } else {
        toast.message('Clic sur un mur ou un escalier pour le supprimer');
      }
      return;
    }

    // SELECT mode : click on empty stage clears selection.
    if (clickedEmpty) clearSelection();
  };

  // Live preview of the wall/calibration being drawn (between origin and cursor)
  const [hoverPos, setHoverPos] = useState(null);
  const handleStageMove = (e) => {
    const stillDrawing = (tool === TOOLS.DRAW_WALL && drawingFrom)
                      || (tool === TOOLS.CALIBRATE && calibrationFrom)
                      || (tool === TOOLS.PLACE_STAIRS && stairsFrom)
                      || (tool === TOOLS.DRAW_TERRAIN && terrainDraft.length > 0);
    if (!stillDrawing) return;
    const stagePos = e.target.getStage().getPointerPosition();
    if (stagePos) setHoverPos(toCanvasPos(stagePos));
  };

  // Double-click anywhere closes the active polyline (terrain or wall chain).
  const handleStageDblClick = () => {
    const st = useGeometryStore.getState();
    if (st.tool === TOOLS.DRAW_TERRAIN && st.terrainDraft.length >= 3) {
      commitTerrain();
      toast.success('Terrain fermé');
      return;
    }
    if (st.tool === TOOLS.DRAW_WALL && st.drawingFrom && st.chainStart) {
      const dx = st.chainStart.x - st.drawingFrom.x;
      const dy = st.chainStart.y - st.drawingFrom.y;
      const dist = Math.hypot(dx, dy);
      // If the chain head is already on the start, just clear. Otherwise
      // add a final closing segment so the user sees a continuous loop.
      if (dist > 3) {
        addWall(st.drawingFrom.x, st.drawingFrom.y, st.chainStart.x, st.chainStart.y, {
          kind: st.drawWallKind,
        });
        toast.success('Pièce fermée');
      } else {
        toast.message('Chaînage terminé');
      }
      setDrawingFrom(null);
      setChainStart(null);
    }
  };

  // ─── Wheel zoom (centered on cursor) + Space-drag pan ───
  const handleWheel = useCallback((e) => {
    e.evt.preventDefault();
    const stage = e.target.getStage();
    const oldScale = viewport.scale;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const mousePointTo = {
      x: (pointer.x - viewport.x) / oldScale,
      y: (pointer.y - viewport.y) / oldScale,
    };
    const direction = e.evt.deltaY > 0 ? -1 : 1;
    const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE,
      oldScale * (direction > 0 ? 1.1 : 1 / 1.1)));
    setViewport({
      scale: newScale,
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  }, [viewport, setViewport]);

  const handleStageDragEnd = (e) => {
    if (!spaceDownRef.current) return;
    setViewport({ scale: viewport.scale, x: e.target.x(), y: e.target.y() });
  };

  // Compute the live preview end point. Endpoint snap to existing walls
  // takes precedence over ortho-snap so the user always feels "connected"
  // to the existing structure when chaining segments.
  const drawPreviewEnd = useMemo(() => {
    if (tool !== TOOLS.DRAW_WALL || !drawingFrom || !hoverPos) return null;
    const snapped = snapToWallEndpoint(hoverPos.x, hoverPos.y, 12);
    if (snapped.snapped) return { x: snapped.x, y: snapped.y, snapped: true };
    if (shiftDownRef.current) {
      const o = orthoSnapPoint(drawingFrom.x, drawingFrom.y, hoverPos.x, hoverPos.y);
      return { x: o.x, y: o.y };
    }
    return hoverPos;
  }, [tool, drawingFrom, hoverPos, snapToWallEndpoint]);

  const calibrationPreviewEnd = useMemo(() => {
    if (tool === TOOLS.CALIBRATE && calibrationFrom && hoverPos) {
      return shiftDownRef.current
        ? orthoSnapPoint(calibrationFrom.x, calibrationFrom.y, hoverPos.x, hoverPos.y)
        : hoverPos;
    }
    return null;
  }, [tool, calibrationFrom, hoverPos]);

  // Crosshair cursor when in special tools
  useEffect(() => {
    const stage = stageRef.current?.getStage?.() || stageRef.current;
    if (!stage) return;
    const container = stage.container ? stage.container() : null;
    if (!container) return;
    if (tool === TOOLS.DRAW_WALL)         container.style.cursor = 'crosshair';
    else if (tool === TOOLS.PLACE_WINDOW) container.style.cursor = 'copy';
    else if (tool === TOOLS.PLACE_DOOR)   container.style.cursor = 'copy';
    else if (tool === TOOLS.PLACE_STAIRS) container.style.cursor = 'crosshair';
    else if (tool === TOOLS.DELETE)       container.style.cursor = 'not-allowed';
    else if (tool === TOOLS.CALIBRATE)    container.style.cursor = 'crosshair';
    else if (tool === TOOLS.DRAW_TERRAIN) container.style.cursor = 'crosshair';
    else if (tool === TOOLS.DRAW_SKEW)    container.style.cursor = 'crosshair';
    else if (tool === TOOLS.PLACE_VELUX)  container.style.cursor = 'crosshair';
    else container.style.cursor = 'default';
  }, [tool]);

  return (
    <div className="relative inline-block rounded-lg border overflow-hidden"
         style={{ borderColor: 'rgba(16,185,129,0.3)', background: '#0a0f18' }}
         data-testid="hybrid-editor">
      <Stage
        ref={stageRef}
        width={CANVAS_W}
        height={CANVAS_H}
        scaleX={viewport.scale}
        scaleY={viewport.scale}
        x={viewport.x}
        y={viewport.y}
        draggable={spaceDownRef.current && tool === TOOLS.SELECT}
        onWheel={handleWheel}
        onDragEnd={handleStageDragEnd}
        onClick={handleStageClick}
        onTap={handleStageClick}
        onDblClick={handleStageDblClick}
        onDblTap={handleStageDblClick}
        onMouseMove={handleStageMove}
      >
        {/* ── Layer 1 — Background ─────────────────────────────────── */}
        <Layer listening={false} data-testid="layer-background">
          {bgImage && (
            <KImage
              image={bgImage}
              x={0} y={0}
              width={backgroundImage?.width || CANVAS_W}
              height={backgroundImage?.height || CANVAS_H}
              opacity={0.3}
            />
          )}
        </Layer>

        {/* ── Layer 2 — VectorOverlay (the real "data") ───────────── */}
        <Layer data-testid="layer-vector-overlay">
          {/* Terrain polygon at the very bottom of the overlay so walls
              and stairs paint clearly on top. */}
          <TerrainShape points={terrain} />

          {/* Windows + Doors below walls so the wall stroke remains visible */}
          {windows.map((win) => (
            <WindowShape key={win.id} window={win} wall={wallById.get(win.wallId)}
                         isSelected={win.id === selectedId}
                         onSelect={() => selectWall(win.id)} />
          ))}
          {doors.map((door) => (
            <DoorShape key={door.id} door={door} wall={wallById.get(door.wallId)}
                       isSelected={door.id === selectedId}
                       onSelect={() => selectWall(door.id)} />
          ))}
          {stairs.map((st) => (
            <StairsShape key={st.id} stairs={st}
                         isSelected={st.id === selectedId}
                         onSelect={() => selectWall(st.id)} />
          ))}

          {walls.map((wall) => (
            <WallShape
              key={wall.id}
              wall={wall}
              isSelected={wall.id === selectedId}
              onClick={() => {
                if (tool === TOOLS.SELECT) selectWall(wall.id);
                else if (tool === TOOLS.DELETE) { deleteWall(wall.id); toast.success('Mur supprimé'); }
                // For PLACE_WINDOW / PLACE_DOOR we deliberately do NOT
                // handle the click here — the stage click handler routes
                // it through `distToSegment` so the menuiserie lands at
                // the exact cursor position (projected t), not the wall
                // midpoint. This is what the user expects when placing
                // multiple openings on a long wall.
              }}
              onEndpointDrag={(endpoint, x, y) => updateWallEndpoint(wall.id, endpoint, x, y)}
              onBodyDrag={(dx, dy) => translateWall(wall.id, dx, dy)}
            />
          ))}

          {/* Sprint 31 — Lignes de brisure (mansardes). Dashed orange
              line, selectable + endpoint-draggable like walls. */}
          {skewEdges.map((edge) => {
            const isSel = edge.id === selectedId;
            const onEndpointDrag = (endpoint, x, y) =>
              updateSkewEdgeEndpoint(edge.id, endpoint, x, y);
            return (
              <SkewEdgeShape
                key={edge.id}
                edge={edge}
                isSelected={isSel}
                onClick={() => {
                  if (tool === TOOLS.SELECT) selectWall(edge.id);
                  else if (tool === TOOLS.DELETE) {
                    deleteSkewEdge(edge.id);
                    toast.success('Ligne de brisure supprimée');
                  }
                }}
                onEndpointDrag={onEndpointDrag}
                onBodyDrag={(dx, dy) => translateSkewEdge(edge.id, dx, dy)}
              />
            );
          })}

          {/* DRAW_SKEW preview */}
          {tool === TOOLS.DRAW_SKEW && drawingFrom && hoverPos && (
            <Line
              points={[drawingFrom.x, drawingFrom.y, hoverPos.x, hoverPos.y]}
              stroke="#fb923c"
              strokeWidth={2}
              dash={[8, 5]}
              opacity={0.85}
              listening={false}
            />
          )}

          {/* Sprint 31 Phase 2 — Velux (fenêtres de toit) rendus après
              leurs skew_edges parents pour qu'ils s'affichent au-dessus. */}
          {veluxes.map((v) => {
            const skew = skewEdges.find((e) => e.id === v.skewId);
            if (!skew) return null;
            return (
              <VeluxShape
                key={v.id}
                velux={v}
                skew={skew}
                scale={scale}
                isSelected={v.id === selectedId}
                onClick={() => {
                  if (tool === TOOLS.SELECT) selectWall(v.id);
                  else if (tool === TOOLS.DELETE) {
                    deleteVelux(v.id);
                    toast.success('Velux supprimé');
                  }
                }}
                onDragT={(t) => updateVelux(v.id, { t })}
              />
            );
          })}

          {/* Draw-wall preview with ortho-snap visible + chain marker.
              `chainStart` shown as a yellow ring so the user can see how
              far they are from closing the polyline. */}
          {tool === TOOLS.DRAW_WALL && chainStart && drawingFrom && (
            chainStart.x !== drawingFrom.x || chainStart.y !== drawingFrom.y
          ) && (
            <Circle
              x={chainStart.x} y={chainStart.y}
              radius={6}
              stroke="#facc15"
              strokeWidth={2}
              fill="rgba(250,204,21,0.18)"
              listening={false}
            />
          )}
          {tool === TOOLS.DRAW_WALL && drawingFrom && drawPreviewEnd && (
            <>
              <Line
                points={[drawingFrom.x, drawingFrom.y, drawPreviewEnd.x, drawPreviewEnd.y]}
                stroke={wallKindFor(drawWallKind).color}
                strokeWidth={3}
                dash={[6, 4]}
                opacity={0.85}
              />
              <Circle x={drawingFrom.x} y={drawingFrom.y} radius={5}
                      fill={wallKindFor(drawWallKind).color} />
              {drawPreviewEnd.snapped && (
                <Circle x={drawPreviewEnd.x} y={drawPreviewEnd.y} radius={8}
                        stroke="#fbbf24" strokeWidth={2}
                        fill="rgba(251,191,36,0.25)" listening={false} />
              )}
            </>
          )}

          {/* Calibration : confirmed segment + live preview during second click */}
          {calibration && (
            <>
              <Line
                points={[calibration.x1, calibration.y1, calibration.x2, calibration.y2]}
                stroke="#facc15"
                strokeWidth={2}
                dash={[8, 4]}
              />
              <Circle x={calibration.x1} y={calibration.y1} radius={4} fill="#facc15" />
              <Circle x={calibration.x2} y={calibration.y2} radius={4} fill="#facc15" />
              <Text
                x={(calibration.x1 + calibration.x2) / 2 + 8}
                y={(calibration.y1 + calibration.y2) / 2 - 14}
                text={`${calibration.meters.toFixed(2)} m`}
                fontSize={12}
                fontStyle="bold"
                fill="#fde047"
                shadowColor="#000"
                shadowBlur={3}
                shadowOpacity={0.8}
              />
            </>
          )}
          {tool === TOOLS.CALIBRATE && calibrationFrom && calibrationPreviewEnd && (
            <>
              <Line
                points={[calibrationFrom.x, calibrationFrom.y, calibrationPreviewEnd.x, calibrationPreviewEnd.y]}
                stroke="#facc15"
                strokeWidth={2}
                dash={[4, 4]}
                opacity={0.85}
              />
              <Circle x={calibrationFrom.x} y={calibrationFrom.y} radius={4} fill="#facc15" />
            </>
          )}

          {/* Stairs preview rectangle while drawing */}
          {tool === TOOLS.PLACE_STAIRS && stairsFrom && hoverPos && (
            <Rect
              x={Math.min(stairsFrom.x, hoverPos.x)}
              y={Math.min(stairsFrom.y, hoverPos.y)}
              width={Math.abs(hoverPos.x - stairsFrom.x)}
              height={Math.abs(hoverPos.y - stairsFrom.y)}
              stroke="#a855f7"
              strokeWidth={1.5}
              dash={[6, 4]}
              fill="rgba(168,85,247,0.1)"
              listening={false}
            />
          )}

          {/* Terrain draft — under construction. Renders the committed
              vertices, the segments connecting them, the live "rubber band"
              segment from the last vertex to the cursor, and a highlight on
              the first vertex when the cursor is within snap distance so the
              user knows where to click to close the polygon. */}
          {tool === TOOLS.DRAW_TERRAIN && terrainDraft.length > 0 && (() => {
            const pts = terrainDraft;
            const flat = [];
            pts.forEach((p) => { flat.push(p.x, p.y); });
            const last = pts[pts.length - 1];
            const showRubber = hoverPos && last;
            let nearFirst = false;
            if (hoverPos && pts.length >= 3) {
              const first = pts[0];
              nearFirst = Math.hypot(hoverPos.x - first.x, hoverPos.y - first.y) <= 12;
            }
            return (
              <>
                {pts.length >= 2 && (
                  <Line points={flat} stroke="#fbbf24" strokeWidth={2}
                        dash={[8, 4]} listening={false} />
                )}
                {showRubber && (
                  <Line
                    points={[last.x, last.y, hoverPos.x, hoverPos.y]}
                    stroke="#fbbf24"
                    strokeWidth={2}
                    dash={[4, 4]}
                    opacity={0.7}
                    listening={false}
                  />
                )}
                {pts.map((p, i) => (
                  <Circle key={`td-${i}`} x={p.x} y={p.y} radius={4}
                          fill={i === 0 ? '#facc15' : '#fbbf24'}
                          stroke={i === 0 && nearFirst ? '#34d399' : '#78350f'}
                          strokeWidth={i === 0 && nearFirst ? 3 : 1}
                          listening={false} />
                ))}
                {nearFirst && (
                  <Text
                    x={pts[0].x + 10}
                    y={pts[0].y - 18}
                    text="Cliquez pour fermer"
                    fontSize={11}
                    fontStyle="bold"
                    fill="#34d399"
                    shadowColor="#000"
                    shadowBlur={3}
                    shadowOpacity={0.8}
                    listening={false}
                  />
                )}
              </>
            );
          })()}
        </Layer>
      </Stage>

      {/* Floating hint at the bottom of the canvas */}
      <div className="absolute left-2 bottom-2 text-[10px] font-mono text-emerald-300/70 select-none">
        {tool === TOOLS.SELECT && 'Drag mur = déplacer · Handles cyan = redimensionner · Molette = zoom · Espace+drag = pan'}
        {tool === TOOLS.DRAW_WALL && (drawingFrom
          ? `Chaînage actif (${wallKindFor(drawWallKind).label}) · Clic = ajouter un segment · Double-clic = fermer la pièce · Shift = ortho · Échap = stop`
          : `Outil ${wallKindFor(drawWallKind).label} · Cliquez le 1ᵉʳ point — chaque clic suivant ajoute un mur · Snap auto aux extrémités existantes`)}
        {tool === TOOLS.PLACE_WINDOW && 'Cliquez à l\u2019endroit exact du mur pour placer la fenêtre — plusieurs par mur possibles'}
        {tool === TOOLS.PLACE_DOOR && 'Cliquez à l\u2019endroit exact du mur pour placer la porte (arc de battement automatique) — plusieurs par mur possibles'}
        {tool === TOOLS.PLACE_STAIRS && (stairsFrom
          ? 'Cliquez le coin opposé pour tracer la zone · puis choisissez le type'
          : 'Cliquez 2 coins pour délimiter la trémie / l\'escalier')}
        {tool === TOOLS.DELETE && 'Cliquez sur un mur ou un escalier pour le supprimer'}
        {tool === TOOLS.CALIBRATE && (calibrationFrom
          ? 'Cliquez le 2ᵉ point puis saisissez la distance réelle'
          : 'Cliquez 2 points dont vous connaissez la distance réelle')}
        {tool === TOOLS.DRAW_TERRAIN && (terrainDraft.length === 0
          ? 'Cliquez chaque sommet du terrain (typique : RDC) · Entrée ou clic sur le 1er point pour fermer'
          : `Sommets : ${terrainDraft.length} · double-clic, Entrée, ou clic sur le 1ᵉʳ point pour fermer · Échap pour annuler`)}
      </div>

      {/* Terrain controls — visible when a terrain polygon is committed.
          Allow the user to drop the polygon without leaving the editor. */}
      {terrain.length > 0 && (
        <div className="absolute left-2 top-2 flex items-center gap-1.5">
          <div
            className="text-[10px] font-mono px-2 py-1 rounded border bg-amber-500/15 border-amber-400/40 text-amber-200"
            data-testid="canvas-terrain-indicator"
          >
            Terrain : {terrain.length} sommets
          </div>
          <button
            type="button"
            onClick={() => { clearTerrain(); toast.message('Terrain effacé'); }}
            title="Effacer le polygone terrain"
            data-testid="canvas-clear-terrain-btn"
            className="text-[10px] font-mono px-2 py-1 rounded border bg-black/40 border-white/15 text-gray-300 hover:bg-white/5 hover:border-amber-400/40"
          >
            Effacer
          </button>
        </div>
      )}

      {/* Top-right info pills : zoom + scale */}
      <div className="absolute right-2 top-2 flex items-center gap-1.5">
        <button type="button"
                onClick={resetViewport}
                title="Réinitialiser le zoom et le pan"
                data-testid="canvas-reset-view-btn"
                className="text-[10px] font-mono px-2 py-1 rounded border bg-black/40 border-white/15 text-gray-300 hover:bg-white/5 hover:border-emerald-400/40">
          {(viewport.scale * 100).toFixed(0)}% ⟲
        </button>
        {scale > 0 && (
          <div className="text-[10px] font-mono px-2 py-1 rounded border bg-emerald-500/15 border-emerald-400/40 text-emerald-200"
               data-testid="canvas-scale-indicator">
            1 m = {(1 / scale).toFixed(1)} px
          </div>
        )}
      </div>

      {/* Inspector — visible whenever a wall/stair/menuiserie is selected */}
      <ElementInspector />
    </div>
  );
};

export default HybridEditor;
